import React, { useState, useEffect, ReactNode, CSSProperties, useRef, useCallback } from 'react';
import { Drawer, DrawerProps } from 'antd';
import './_bottom-drawer.scss';

interface BottomDrawerProps extends Omit<DrawerProps, 'placement'> {
  visible: boolean;
  onClose: () => void;
  children: ReactNode;
  height?: string | number;
  dragThreshold?: number;
  handleStyle?: CSSProperties;
  className?: string;
  expandable?: boolean;
  allowDragCloseOnFullView?: boolean;
}


const BottomDrawer: React.FC<BottomDrawerProps> = ({
  visible,
  onClose,
  children,
  height = '50%',
  dragThreshold = 100,
  handleStyle = {},
  className = '',
  expandable = false,
  allowDragCloseOnFullView = false,
  ...props
}) => {
  const [dragging, setDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [deltaY, setDeltaY] = useState(0);
  const [initialHeight, setInitialHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const drawerBodyRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!visible) {
      setIsExpanded(false);
      return;
    }

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.classList.add('drawer-open-lock');

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.classList.remove('drawer-open-lock');
    };
  }, [visible]);

  const findScrollableElements = useCallback((container: HTMLElement): HTMLElement[] => {
    const results: HTMLElement[] = [];
    
    const collectScrollableElements = (element: HTMLElement) => {
      const style = window.getComputedStyle(element);
      const overflowY = style.overflowY;
      
      if ((overflowY === 'auto' || overflowY === 'scroll') && 
          element.scrollHeight > element.clientHeight) {
        results.push(element);
      }
      
      if (style.display !== 'none') {
        Array.from(element.children).forEach(child => {
          collectScrollableElements(child as HTMLElement);
        });
      }
    };
    
    collectScrollableElements(container);
    return results;
  }, []);
  
  const isFullView = height === '100%';

  const canDragToClose = useCallback((): boolean => {
    if (isFullView && !allowDragCloseOnFullView) return false;
    if (!drawerContentRef.current) return false;
    
    const drawerBody = drawerBodyRef.current;
    if (drawerBody && drawerBody.scrollTop > 0) {
      return false;
    }
    
    const scrollableElements = findScrollableElements(drawerContentRef.current);
    
    return !scrollableElements.some(el => el.scrollTop > 0);
  }, [findScrollableElements, isFullView, allowDragCloseOnFullView]);
 
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, textarea, select, label, [role="button"], [data-drag-ignore]')) {
      return;
    }

    if (!canDragToClose()) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    if (drawerContentRef.current) {
      setInitialHeight(drawerContentRef.current.clientHeight);
    }
    
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    setStartY(clientY);
    setDragging(true);
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!dragging) return;
    
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    const diff = clientY - startY;
    
    if (expandable) {
      if (isExpanded) {
        if (diff > 0) {
          setDeltaY(diff);
          e.preventDefault();
        }
      } else {
        setDeltaY(diff);
        e.preventDefault();
      }
    } else {
      if (diff > 0) {
        setDeltaY(diff);
        e.preventDefault();
      }
    }
  };

  const handleDragEnd = () => {
    if (!dragging) return;
    
    setDragging(false);
    if (deltaY > dragThreshold) {
      if (expandable && isExpanded) {
        setIsExpanded(false);
      } else {
        onClose();
      }
    } else if (deltaY < -dragThreshold && expandable && !isExpanded) {
      setIsExpanded(true);
    }
    setDeltaY(0);
  };

  const handleHandleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (isFullView) return;
    e.preventDefault();
    e.stopPropagation();
    
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    setStartY(clientY);
    setDragging(true);
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleDragMove, { passive: false });
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [dragging, startY, deltaY]);

  const bodyStyle = {
    padding: 0,
    position: 'relative' as const,
    backgroundColor: 'transparent',
    overflow: 'hidden' as const,
    transform: `translateY(${deltaY > 0 ? deltaY : 0}px)`,
    transition: dragging ? 'none' : 'transform 0.3s ease',
  };

  const drawerClasses = `bottom-drawer-container ${className} ${expandable && isExpanded ? 'is-expanded' : ''}`;

  const getDynamicHeight = () => {
    if (dragging && deltaY < 0 && expandable && !isExpanded) {
      return initialHeight - deltaY;
    }
    return expandable && isExpanded ? '96%' : height;
  };

  return (
    <Drawer
      placement="bottom"
      height={getDynamicHeight()}
      onClose={onClose}
      visible={visible}
      closable={false}
      bodyStyle={bodyStyle}
      className={drawerClasses}
      {...props}
    >
      <div 
        className="drawer-content"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        ref={drawerContentRef}
      >
        <div 
          className="drawer-handle"
          onMouseDown={handleHandleDragStart}
          onTouchStart={handleHandleDragStart}
          style={handleStyle}
        >
          <div className="handle-bar" />
        </div>

        <div className="drawer-body" ref={drawerBodyRef}>
          {children}
        </div>
      </div>
    </Drawer>
  );
};

export default BottomDrawer; 